import { Fragment, useState } from "react";
import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  IconButton,
  Link,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  AUTO_ACK_LIMITS,
  AUTO_ACK_USER_KEY,
  buildNamePattern,
  IDepartmentData,
  IUserInfo,
  patternToValue,
} from "@home-health-notify/domain";
import { IAutoAckCriteria } from "@home-health-notify/models";
import { useTranslation } from "hhn-commons";
import {
  departmentLabel,
  matchedUsers,
  selectableUserTypes,
  userChipProps,
  userLabelWithStatus,
  userLabelWithTypeAndStatus,
  userSecondaryLine,
  userStatusSx,
} from "./autoAckUtils";

// Past this the preview stops being scannable and the count carries the rest.
const NAME_MATCH_LIMIT = 8;

// The cap is on the stored pattern, which carries the word boundaries too, so
// the admin's own budget is what is left after them.
const NAME_LENGTH_BUDGET =
  AUTO_ACK_LIMITS.MAX_REGEX_LENGTH - buildNamePattern("").length;

// The keys an admin can pick from, in the order they appear in the "Who" list.
// Naming people outright is the common case, so it leads and is what a new
// condition row starts on.
export const WHO_KEYS: string[] = [
  AUTO_ACK_USER_KEY.USER_ID,
  AUTO_ACK_USER_KEY.LAST_NAME,
  AUTO_ACK_USER_KEY.FIRST_NAME,
  AUTO_ACK_USER_KEY.TYPE,
  AUTO_ACK_USER_KEY.DEPARTMENT_ID,
];

// Two rows of small chips. The value box is held at this height so picking
// values never reflows the conditions below it.
const VALUE_BOX_HEIGHT = 72;
const stableValueBox = {
  flex: 1,
  "& .MuiOutlinedInput-root": {
    minHeight: VALUE_BOX_HEIGHT,
    maxHeight: VALUE_BOX_HEIGHT,
    overflowY: "auto",
    alignItems: "flex-start",
    alignContent: "flex-start",
  },
};

export const emptyCriteria = (key: string): IAutoAckCriteria => ({
  key,
  values: [],
  ...(key === AUTO_ACK_USER_KEY.FIRST_NAME ||
  key === AUTO_ACK_USER_KEY.LAST_NAME
    ? { isRegex: true }
    : {}),
});

type Props = {
  criteria: IAutoAckCriteria;
  users: IUserInfo[];
  departments: IDepartmentData[];
  // Keys already used by sibling rows — the backend rejects a repeated key
  // inside one group, so they are hidden rather than explained after the fact.
  usedKeys: string[];
  showErrors: boolean;
  onChange: (criteria: IAutoAckCriteria) => void;
  onRemove?: () => void;
};

const ConditionRow = ({
  criteria,
  users,
  departments,
  usedKeys,
  showErrors,
  onChange,
  onRemove,
}: Props) => {
  const { t } = useTranslation();
  const tNs = (key: string, options?: object) =>
    t(key, { ns: "autoAck", ...options });
  const [showAllMatches, setShowAllMatches] = useState(false);

  const values = criteria.values ?? [];
  const isName =
    criteria.key === AUTO_ACK_USER_KEY.FIRST_NAME ||
    criteria.key === AUTO_ACK_USER_KEY.LAST_NAME;
  const typed = isName ? patternToValue(values[0] ?? "") : "";
  const regexTooLong =
    isName && buildNamePattern(typed).length > AUTO_ACK_LIMITS.MAX_REGEX_LENGTH;
  const tooManyValues = values.length > AUTO_ACK_LIMITS.MAX_VALUES_PER_GROUP;
  const isEmpty = isName ? !typed.trim() : !values.length;
  const hasError = showErrors && (isEmpty || regexTooLong || tooManyValues);

  const helper = () => {
    if (!showErrors) return undefined;
    if (isEmpty) return tNs("conditionEmpty");
    if (regexTooLong) return tNs("valueTooLong", { max: NAME_LENGTH_BUDGET });
    if (tooManyValues)
      return tNs("tooManyValues", {
        max: AUTO_ACK_LIMITS.MAX_VALUES_PER_GROUP,
      });
    return undefined;
  };

  const setValues = (next: string[]) => onChange({ ...criteria, values: next });

  const valueControl = () => {
    if (criteria.key === AUTO_ACK_USER_KEY.TYPE) {
      return (
        <Autocomplete
          multiple
          disableCloseOnSelect
          sx={stableValueBox}
          options={selectableUserTypes}
          value={values}
          onChange={(_, next) => setValues(next)}
          getOptionLabel={type => tNs(`userType.${type}`)}
          renderInput={params => (
            <TextField
              {...params}
              size="small"
              placeholder={values.length ? undefined : tNs("choose")}
              error={hasError}
              helperText={helper()}
            />
          )}
        />
      );
    }

    if (criteria.key === AUTO_ACK_USER_KEY.DEPARTMENT_ID) {
      return (
        <Autocomplete
          multiple
          disableCloseOnSelect
          sx={stableValueBox}
          options={departments.map(department => department.id)}
          value={values}
          onChange={(_, next) => setValues(next)}
          getOptionLabel={id => departmentLabel(id, departments)}
          renderInput={params => (
            <TextField
              {...params}
              size="small"
              placeholder={values.length ? undefined : tNs("choose")}
              error={hasError}
              helperText={helper()}
            />
          )}
        />
      );
    }

    if (criteria.key === AUTO_ACK_USER_KEY.USER_ID) {
      const selected = users.filter(user => values.includes(user.id));
      return (
        <Autocomplete
          multiple
          disableCloseOnSelect
          sx={stableValueBox}
          options={users}
          value={selected}
          onChange={(_, next) => setValues(next.map(user => user.id))}
          getOptionLabel={option => userLabelWithTypeAndStatus(option, tNs)}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderOption={(props, option, { selected: isSelected }) => (
            <li {...props} key={option.id}>
              <Checkbox checked={isSelected} sx={{ mr: 1 }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={userStatusSx(option)}>
                  {userLabelWithStatus(option, tNs)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {userSecondaryLine(option, tNs)}
                </Typography>
              </Box>
            </li>
          )}
          renderValue={(value, getItemProps) =>
            value.map((option, index) => (
              <Chip
                {...getItemProps({ index })}
                key={option.id}
                size="small"
                label={userLabelWithTypeAndStatus(option, tNs)}
                {...userChipProps(option)}
              />
            ))
          }
          renderInput={params => (
            <TextField
              {...params}
              size="small"
              placeholder={selected.length ? undefined : tNs("choose")}
              error={hasError}
              helperText={helper()}
            />
          )}
        />
      );
    }

    // A typed fragment becomes a regex the admin never sees, so the people it
    // currently matches are listed instead — otherwise there is no way to tell
    // "ben" from a typo before saving.
    const matched = typed.trim() ? matchedUsers([[criteria]], users) : [];

    return (
      <Box sx={{ flex: 1 }}>
        <TextField
          fullWidth
          size="small"
          placeholder={tNs("namePlaceholder")}
          value={typed}
          onChange={event =>
            setValues(
              event.target.value ? [buildNamePattern(event.target.value)] : [],
            )
          }
          error={hasError}
          helperText={helper() ?? tNs("nameHelper")}
        />
        {!!typed.trim() && (
          <Typography
            variant="caption"
            color={matched.length ? "text.secondary" : "warning.main"}
            sx={{ display: "block", mt: 0.25 }}
          >
            {tNs("nameMatches", { count: matched.length })}{" "}
            {(showAllMatches
              ? matched
              : matched.slice(0, NAME_MATCH_LIMIT)
            ).map((user, index) => (
              <Fragment key={user.id}>
                {index > 0 && ", "}
                <Box component="span" sx={userStatusSx(user)}>
                  {userLabelWithTypeAndStatus(user, tNs)}
                </Box>
              </Fragment>
            ))}
            {matched.length > NAME_MATCH_LIMIT && (
              <Link
                component="button"
                type="button"
                variant="caption"
                underline="hover"
                onClick={() => setShowAllMatches(previous => !previous)}
                sx={{ ml: 0.75, verticalAlign: "baseline" }}
              >
                {showAllMatches
                  ? tNs("showFewer")
                  : tNs("showAll", {
                      count: matched.length - NAME_MATCH_LIMIT,
                    })}
              </Link>
            )}
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
      <TextField
        select
        size="small"
        label={tNs("who")}
        sx={{ width: 260 }}
        value={criteria.key}
        onChange={event => onChange(emptyCriteria(event.target.value))}
      >
        {WHO_KEYS.filter(
          key => key === criteria.key || !usedKeys.includes(key),
        ).map(key => (
          <MenuItem key={key} value={key}>
            {tNs(`who.${key}`)}
          </MenuItem>
        ))}
      </TextField>
      {valueControl()}
      {onRemove && (
        <IconButton
          size="small"
          color="error"
          onClick={onRemove}
          aria-label={tNs("removeCondition")}
          sx={{ mt: 0.5 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
};

export default ConditionRow;
